import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, InputBase, Typography } from "@mui/material";
import { useRef } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { v4 } from "uuid";
import { CHECKLIST_QUESTION_TYPE } from "../../constants";
import { IChecklistQuestionType } from "../../interfaces/CheckLists";
import { useLocale } from "../../providers/LocaleProvider";
import { ChecklistQuestionType } from "../../utilities/checkListsSchema";
import { Button } from "../Button";
import { Paper } from "../Paper";
import QuestionItem from "./QuestionItem";

interface SectionContentProps {
  sectionIndex: number;
  listQuestionTypes?: IChecklistQuestionType[];
  name: string;
  activeId?: string;
  setActiveId?: (id: string) => void;
  triggerValidationValueDebounced?: () => void;
}

export default function SectionContent({
  sectionIndex,
  listQuestionTypes,
  name,
  activeId,
  setActiveId,
  triggerValidationValueDebounced
}: SectionContentProps) {
  const optionsBackupRef = useRef<Map<string, any[]>>(new Map());
  const { translate } = useLocale();

  const { watch, control, setFocus } = useFormContext();
  const {
    append: appendQuestion,
    replace: replaceQuestion,
    update: updateQuestion
  } = useFieldArray({
    control,
    name: `${name}.questions`
  });

  const defaultQuestionType = listQuestionTypes?.[0]?.id;
  const questions = watch(`${name}.questions`);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const handleAddQuestion = () => {
    appendQuestion({
      id: v4(),
      checklistQuestionTypeId: defaultQuestionType,
      questionText: ``,
      displayOrder: questions?.length,
      isRequired: false,
      questionTypeCode:
        listQuestionTypes?.find((i: IChecklistQuestionType) => i.id === defaultQuestionType)
          ?.code || "",
      options: [{ id: v4(), optionText: "", displayOrder: 0 }]
    });
    setFocus(`${name}.questions.${questions?.length}.questionText`);

    triggerValidationValueDebounced?.();
  };

  const handleChangeQuestionType = (
    questionIndex: number,
    prevTypeId: string,
    nextTypeId: string
  ) => {
    const currentQuestion = questions?.[questionIndex];
    if (!currentQuestion) return;

    const prevTypeCode = listQuestionTypes?.find(
      (i: IChecklistQuestionType) => i.id === prevTypeId
    )?.code;
    const nextTypeCode = listQuestionTypes?.find(
      (i: IChecklistQuestionType) => i.id === nextTypeId
    )?.code;

    const isOptionType = (code?: string) =>
      code === CHECKLIST_QUESTION_TYPE.SINGLE_CHOICE ||
      code === CHECKLIST_QUESTION_TYPE.MULTIPLE_CHOICE;

    const switchingFromOptionToNonOption =
      isOptionType(prevTypeCode) && !isOptionType(nextTypeCode);
    const switchingFromNonOptionToOption =
      !isOptionType(prevTypeCode) && isOptionType(nextTypeCode);

    let newOptions = currentQuestion.options;

    if (switchingFromOptionToNonOption) {
      const existingOptions = currentQuestion.options || [];
      if (existingOptions.length > 0) {
        optionsBackupRef.current.set(currentQuestion.id, existingOptions);
      }
      newOptions = [];
    } else if (switchingFromNonOptionToOption) {
      const restored = optionsBackupRef.current.get(currentQuestion.id);
      newOptions =
        restored && restored.length > 0
          ? restored.map((opt: any, idx: number) => ({ ...opt, displayOrder: idx }))
          : [{ id: v4(), optionText: "", displayOrder: 0 }];
    }

    updateQuestion(questionIndex, {
      ...currentQuestion,
      checklistQuestionTypeId: nextTypeId,
      questionTypeCode: nextTypeCode || "",
      options: newOptions
    });

    triggerValidationValueDebounced?.();
  };

  const handleRemoveQuestionWithReindex = (removeIndex: number) => {
    const remaining = (questions || [])
      .filter((_: any, idx: number) => idx !== removeIndex)
      .map((q: any, idx: number) => ({ ...q, displayOrder: idx }));
    replaceQuestion(remaining);
    triggerValidationValueDebounced?.();
  };

  const handleQuestionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = questions?.findIndex((item: any) => item.id === active.id);
      const newIndex = questions?.findIndex((item: any) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedQuestions = arrayMove(questions, oldIndex, newIndex);
        const finalQuestions = reorderedQuestions.map((question: any, index: number) => ({
          ...question,
          displayOrder: index
        }));
        replaceQuestion(finalQuestions);
      }
    }
    setActiveId?.("");
    triggerValidationValueDebounced?.();
  };
  const handleQuestionDragStart = (event: DragStartEvent) => {
    setActiveId?.(String(event.active.id));
  };

  const activeQuestion = questions?.find((item: any) => item.id === activeId);
  return (
    <>
      <Box sx={{ marginBottom: 3 }}>
        <Controller
          control={control}
          name={`${name}.title`}
          render={({ field, fieldState }) => (
            <InputBase
              multiline
              inputRef={field.ref}
              className='klayo-paper_title'
              value={field.value}
              fullWidth={false}
              placeholder={translate("settingsViews.checklists.sectionPlaceholder", {
                number: sectionIndex + 1
              })}
              onChange={(e) => {
                field.onChange(e);
                triggerValidationValueDebounced?.();
              }}
              error={!!fieldState.error}
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                "& input": {
                  fontSize: "18px",
                  fontWeight: 600,
                  width: "100%"
                },
                borderRadius: "8px",
                border: fieldState.error
                  ? "1px solid var(--torch-red-tr-500)"
                  : "1px solid transparent",
                width: "100%",
                padding: "6px 10px 6px 6px"
              }}
            />
          )}
        />
      </Box>

      <DndContext
        sensors={sensors}
        onDragStart={handleQuestionDragStart}
        onDragEnd={handleQuestionDragEnd}
      >
        <SortableContext
          items={questions?.map((item: any) => item.id) || []}
          strategy={verticalListSortingStrategy}
        >
          {questions?.map((question: any, qIndex: number) => (
            <Box key={question.id}>
              <QuestionItem
                sectionIndex={sectionIndex}
                qIndex={qIndex}
                onRemoveQuestion={() => handleRemoveQuestionWithReindex(qIndex)}
                canRemove={questions?.length > 1}
                listQuestionTypes={listQuestionTypes}
                onChangeType={handleChangeQuestionType}
                name={`${name}.questions.${qIndex}`}
                question={question}
                triggerValidationValueDebounced={triggerValidationValueDebounced}
              />
            </Box>
          ))}
          <DragOverlay zIndex={2000} dropAnimation={null}>
            {activeQuestion ? (
              <CustomQuestionPreview
                question={activeQuestion}
                listQuestionTypes={listQuestionTypes}
              />
            ) : null}
          </DragOverlay>
        </SortableContext>
      </DndContext>

      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Button
          size='md'
          variant='text'
          label={translate("common.newQuestion")}
          startIcon={<AddIcon />}
          type='button'
          onClick={handleAddQuestion}
        />
      </Box>
    </>
  );
}
const CustomQuestionPreview = ({
  question,
  listQuestionTypes
}: {
  question: ChecklistQuestionType;
  listQuestionTypes?: IChecklistQuestionType[];
}) => {
  const { translate } = useLocale();
  return (
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: 2,
        borderRadius: "8px",
        width: "60%",
        cursor: "pointer",
        "&:hover .remove-icon": {
          opacity: 1
        },
        p: "12px !important",
        border: "1px solid #3b82f6",
        rotate: "2deg"
      }}
    >
      <Box display='flex' alignItems='center' gap={1} justifyContent='space-between' width='100%'>
        <Box sx={{ color: "grey.500" }} display='flex' alignItems='center' gap={1}>
          <DragIndicatorIcon fontSize='small' />
          <Typography className='klayo-paper_title' variant='body1' fontWeight={500} noWrap>
            {question?.questionText ||
              translate("settingsViews.checklists.questionPlaceholder", {
                number: question.displayOrder + 1
              })}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: "grey.200", padding: "4px 8px", borderRadius: "4px" }}>
          <Typography
            className='klayo-paper_title'
            sx={{ fontSize: "14px" }}
            fontWeight={500}
            noWrap
          >
            {listQuestionTypes?.find((i) => i.id === question.checklistQuestionTypeId)?.name ||
              translate("settingsViews.checklists.type")}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
